/**
 * Google Drive Cleanup Service
 * 
 * This service provides cleanup functionality for Google Drive files and folders
 * that may be causing backend performance issues.
 */

class GoogleDriveCleanup {
  constructor(googleDriveAPI) {
    this.driveAPI = googleDriveAPI
  }

  /**
   * Get cleanup statistics for Google Drive files
   */
  async getCleanupStats() {
    if (!this.driveAPI.accessToken) {
      throw new Error('Google Drive API not initialized')
    }

    try {
      console.log('🔍 GoogleDriveCleanup: Scanning Google Drive for cleanup statistics...')
      
      // Ensure app folder structure is ready
      await this.driveAPI.ensureAppFolderStructure()
      
      // Get the app folder ID from the driveAPI instance
      const appFolderId = this.driveAPI.appFolderId
      if (!appFolderId) {
        throw new Error('App folder not found')
      }

      // Search for all files in the app folder using the API request method
      const query = `'${appFolderId}' in parents and trashed=false`
      const searchResponse = await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files`, {
        q: query,
        fields: 'files(id, name, size, mimeType)',
        pageSize: 1000
      })
      
      if (!searchResponse.ok) {
        throw new Error(`Failed to search files: ${searchResponse.status}`)
      }
      
      const result = await searchResponse.json()
      const files = result.files || []
      
      // Analyze files for cleanup
      const stats = {
        totalFiles: files.length,
        suspiciousFiles: 0,
        emptyFiles: 0,
        deletedNoteFiles: 0,
        updatedNoteFiles: 0,
        softDeletedFiles: 0
      }

      // Count suspicious files (empty or malformed)
      for (const file of files) {
        let isSuspicious = false
        
        // Only mark as suspicious if it's actually problematic
        if (file.size === '0' || !file.size) {
          stats.emptyFiles++
          
          // Additional checks to avoid false positives
          if (file.mimeType === 'application/vnd.google-apps.folder') {
            // Folders can legitimately be empty
            isSuspicious = false
            console.log(`🔍 GoogleDriveCleanup: Empty folder "${file.name}" - not suspicious (folders can be empty)`)
          } else if (file.name.endsWith('.json')) {
            // Empty JSON files are suspicious
            isSuspicious = true
            console.log(`⚠️ GoogleDriveCleanup: Empty JSON file "${file.name}" - marked as suspicious`)
          } else if (file.name.includes('undefined') || file.name.includes('null')) {
            // Files with suspicious names are suspicious
            isSuspicious = true
            console.log(`⚠️ GoogleDriveCleanup: File with suspicious name "${file.name}" - marked as suspicious`)
          } else {
            // Other empty files might be normal (like placeholder files)
            isSuspicious = false
            console.log(`🔍 GoogleDriveCleanup: Empty file "${file.name}" - not suspicious (might be normal)`)
          }
        }
        
        if (isSuspicious) {
          stats.suspiciousFiles++
        }
      }

      // Count files in soft-delete folder
      try {
        const deletedFolderId = this.driveAPI.folderCache.get('DELETED')
        if (deletedFolderId) {
          const deletedQuery = `'${deletedFolderId}' in parents and trashed=false`
          const deletedResponse = await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files`, {
            q: deletedQuery,
            fields: 'files(id)',
            pageSize: 1000
          })
          
          if (deletedResponse.ok) {
            const deletedResult = await deletedResponse.json()
            stats.softDeletedFiles = deletedResult.files?.length || 0
            console.log(`🔍 GoogleDriveCleanup: Found ${stats.softDeletedFiles} files in soft-delete folder`)
          }
        }
      } catch (error) {
        console.warn('⚠️ GoogleDriveCleanup: Failed to count soft-delete files:', error)
        stats.softDeletedFiles = 0
      }

      console.log(`📊 GoogleDriveCleanup: Found ${stats.totalFiles} total files, ${stats.suspiciousFiles} suspicious files, ${stats.softDeletedFiles} soft-deleted files`)
      return stats
    } catch (error) {
      console.error('❌ GoogleDriveCleanup: Failed to get stats:', error)
      throw error
    }
  }

  /**
   * Clean up corrupted files in Google Drive
   */
  async cleanupCorruptedFiles() {
    if (!this.driveAPI.accessToken) {
      throw new Error('Google Drive API not initialized')
    }

    try {
      console.log('🧹 GoogleDriveCleanup: Starting cleanup of corrupted files...')
      
      // Ensure app folder structure is ready
      await this.driveAPI.ensureAppFolderStructure()
      
      // Get the app folder ID from the driveAPI instance
      const appFolderId = this.driveAPI.appFolderId
      if (!appFolderId) {
        throw new Error('App folder not found')
      }

      // Search for suspicious files
      const query = `'${appFolderId}' in parents and trashed=false`
      const searchResponse = await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files`, {
        q: query,
        fields: 'files(id, name, size, mimeType)',
        pageSize: 1000
      })
      
      if (!searchResponse.ok) {
        throw new Error(`Failed to search files: ${searchResponse.status}`)
      }
      
      const result = await searchResponse.json()
      const allFiles = result.files || []
      
      // Filter for suspicious files in JavaScript instead of API query
      const suspiciousFiles = allFiles.filter(file => {
        // Only mark as suspicious if it's actually problematic
        if (file.size === '0' || !file.size) {
          if (file.mimeType === 'application/vnd.google-apps.folder') {
            // Folders can legitimately be empty
            return false
          } else if (file.name.endsWith('.json')) {
            // Empty JSON files are suspicious
            return true
          } else if (file.name.includes('undefined') || file.name.includes('null')) {
            // Files with suspicious names are suspicious
            return true
          } else {
            // Other empty files might be normal (like placeholder files)
            return false
          }
        }
        return false
      })
      
      console.log(`🔍 GoogleDriveCleanup: Found ${allFiles.length} total files, filtered to ${suspiciousFiles.length} suspicious files`)

      if (suspiciousFiles.length === 0) {
        return {
          cancelled: false,
          cleaned: 0,
          message: 'No corrupted files found'
        }
      }

      console.log(`🧹 GoogleDriveCleanup: Found ${suspiciousFiles.length} suspicious files to clean up`)
      
      // Move suspicious files to trash
      let cleanedCount = 0
      for (const file of suspiciousFiles) {
        try {
          console.log(`🗑️ GoogleDriveCleanup: Moving file to trash: ${file.name}`)
          
          // Move to trash by updating the file
          await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files/${file.id}`, {}, {
            method: 'PATCH',
            body: JSON.stringify({ trashed: true }),
            headers: { 'Content-Type': 'application/json' }
          })
          
          cleanedCount++
          console.log(`✅ GoogleDriveCleanup: Successfully moved to trash: ${file.name}`)
        } catch (error) {
          console.error(`❌ GoogleDriveCleanup: Failed to move file to trash: ${file.name}`, error)
        }
      }

      console.log(`🧹 GoogleDriveCleanup: Cleanup complete! Moved ${cleanedCount} files to trash`)
      
      return { 
        cancelled: false,
        cleaned: cleanedCount, 
        message: `Successfully cleaned up ${cleanedCount} corrupted files`
      }
    } catch (error) {
      console.error('❌ GoogleDriveCleanup: Failed to cleanup files:', error)
      throw error
    }
  }

  /**
   * Clean up duplicate/corrupted folders in Google Drive
   */
  async cleanupDuplicateFolders() {
    if (!this.driveAPI.accessToken) {
      throw new Error('Google Drive API not initialized')
    }

    try {
      console.log('🧹 GoogleDriveCleanup: Starting comprehensive folder cleanup...')
      
      // First, find all app folders with the same name (this is the main issue)
      const appFolderQuery = `name='${this.driveAPI.APP_FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`
      const appFolderResponse = await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files`, {
        q: appFolderQuery,
        fields: 'files(id, name, mimeType, createdTime)',
        pageSize: 1000
      })
      
      if (!appFolderResponse.ok) {
        throw new Error(`Failed to search for app folders: ${appFolderResponse.status}`)
      }
      
      const appFolderResult = await appFolderResponse.json()
      const appFolders = appFolderResult.files || []
      
      console.log(`🔍 GoogleDriveCleanup: Found ${appFolders.length} app folders with name "${this.driveAPI.APP_FOLDER_NAME}"`)
      
      if (appFolders.length <= 1) {
        console.log('✅ GoogleDriveCleanup: No duplicate app folders found')
        
        // Check for duplicate subfolders within the single app folder
        return await this.cleanupDuplicateSubfolders()
      }
      
      // Sort by creation time to keep the oldest (most likely the original)
      appFolders.sort((a, b) => new Date(a.createdTime) - new Date(b.createdTime))
      const originalAppFolder = appFolders[0]
      const duplicateAppFolders = appFolders.slice(1)
      
      console.log(`🔍 GoogleDriveCleanup: Keeping original app folder: ${originalAppFolder.id} (created: ${originalAppFolder.createdTime})`)
      console.log(`🧹 GoogleDriveCleanup: Found ${duplicateAppFolders.length} duplicate app folders to clean up`)
      
      // Clean up duplicate app folders - be more aggressive
      let deletedCount = 0
      let skippedCount = 0
      
      for (const duplicateAppFolder of duplicateAppFolders) {
        try {
          console.log(`🔍 GoogleDriveCleanup: Processing duplicate app folder: ${duplicateAppFolder.id}`)
          
          // Check if the duplicate app folder is empty
          const folderContentsQuery = `'${duplicateAppFolder.id}' in parents and trashed=false`
          const folderContentsResponse = await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files`, {
            q: folderContentsQuery,
            fields: 'files(id, name, mimeType, size)',
            pageSize: 1000
          })
          
          if (!folderContentsResponse.ok) {
            console.warn(`⚠️ GoogleDriveCleanup: Failed to check app folder contents for ${duplicateAppFolder.id}`)
            continue
          }
          
          const folderContents = await folderContentsResponse.json()
          const folderContentsList = folderContents.files || []
          
          console.log(`🔍 GoogleDriveCleanup: Folder ${duplicateAppFolder.id} contains ${folderContentsList.length} items:`)
          folderContentsList.forEach(item => {
            console.log(`  - ${item.name} (${item.mimeType}, size: ${item.size})`)
          })
          
          if (folderContentsList.length === 0) {
            // Empty duplicate app folder, safe to delete
            console.log(`🗑️ GoogleDriveCleanup: Deleting empty duplicate app folder: ${duplicateAppFolder.id}`)
            await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files/${duplicateAppFolder.id}`, null, { method: 'DELETE' })
            deletedCount++
            console.log(`✅ GoogleDriveCleanup: Successfully deleted empty duplicate app folder: ${duplicateAppFolder.id}`)
          } else {
            // Non-empty folder - let's be more aggressive and check what's inside
            console.log(`⚠️ GoogleDriveCleanup: Found non-empty duplicate app folder: ${duplicateAppFolder.id} (${folderContentsList.length} items)`)
            
            // Check if it's just empty subfolders or actual content
            let hasRealContent = false
            let emptySubfolderCount = 0
            
            for (const item of folderContentsList) {
              if (item.mimeType === 'application/vnd.google-apps.folder') {
                // Check if this subfolder is empty
                const subfolderContentsQuery = `'${item.id}' in parents and trashed=false`
                const subfolderContentsResponse = await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files`, {
                  q: subfolderContentsQuery,
                  fields: 'files(id, name)',
                  pageSize: 1
                })
                
                if (subfolderContentsResponse.ok) {
                  const subfolderContents = await subfolderContentsResponse.json()
                  const subfolderFiles = subfolderContents.files || []
                  
                  if (subfolderFiles.length === 0) {
                    emptySubfolderCount++
                    console.log(`  - Subfolder "${item.name}" is empty`)
                  } else {
                    console.log(`  - Subfolder "${item.name}" contains ${subfolderFiles.length} items`)
                    hasRealContent = true
                  }
                }
              } else {
                // It's a file, not a folder
                hasRealContent = true
                console.log(`  - File "${item.name}" indicates real content`)
              }
            }
            
            console.log(`📊 GoogleDriveCleanup: Analysis - Empty subfolders: ${emptySubfolderCount}, Has real content: ${hasRealContent}`)
            
            if (!hasRealContent && emptySubfolderCount === folderContentsList.length) {
              console.log(`🧹 GoogleDriveCleanup: Folder contains only empty subfolders, deleting: ${duplicateAppFolder.id}`)
              // Delete the folder and all its contents
              await this.deleteFolderRecursively(duplicateAppFolder.id)
              deletedCount++
              console.log(`✅ GoogleDriveCleanup: Successfully deleted duplicate app folder with empty subfolders: ${duplicateAppFolder.id}`)
            } else {
              // Force cleanup: Since this is a duplicate app folder, we should delete it anyway
              // The original app folder should contain the real data
              console.log(`🧹 GoogleDriveCleanup: Force deleting duplicate app folder: ${duplicateAppFolder.id} (even though it contains content)`)
              console.log(`💡 GoogleDriveCleanup: This is safe because we're keeping the original app folder: ${originalAppFolder.id}`)
              
              try {
                await this.deleteFolderRecursively(duplicateAppFolder.id)
                deletedCount++
                console.log(`✅ GoogleDriveCleanup: Successfully force deleted duplicate app folder: ${duplicateAppFolder.id}`)
              } catch (error) {
                console.warn(`⚠️ GoogleDriveCleanup: Force deletion failed for ${duplicateAppFolder.id}:`, error)
                skippedCount++
              }
            }
          }
        } catch (error) {
          console.warn(`⚠️ GoogleDriveCleanup: Failed to process duplicate app folder ${duplicateAppFolder.id}:`, error)
        }
      }
      
      console.log(`📊 GoogleDriveCleanup: App folder cleanup summary: ${deletedCount} deleted, ${skippedCount} skipped`)
      
      // Now clean up duplicate subfolders within the remaining app folder
      const subfolderResult = await this.cleanupDuplicateSubfolders()
      
      return {
        success: true,
        deletedCount: deletedCount + subfolderResult.deletedCount,
        message: `Cleaned up ${deletedCount} duplicate app folders and ${subfolderResult.deletedCount} duplicate subfolders. ${skippedCount} folders skipped due to errors.`
      }
    } catch (error) {
      console.error('❌ GoogleDriveCleanup: Failed to cleanup folders:', error)
      return {
        success: false,
        message: `Folder cleanup failed: ${error.message}`,
        error: error.message
      }
    }
  }

  /**
   * Clean up duplicate subfolders within a single app folder
   */
  async cleanupDuplicateSubfolders() {
    try {
      // Ensure app folder structure is ready
      await this.driveAPI.ensureAppFolderStructure()
      
      // Get the app folder ID from the driveAPI instance
      const appFolderId = this.driveAPI.appFolderId
      if (!appFolderId) {
        throw new Error('App folder not found')
      }

      // Search for all subfolders in the app folder
      const folderQuery = `'${appFolderId}' in parents and trashed=false and mimeType='application/vnd.google-apps.folder'`
      const searchResponse = await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files`, {
        q: folderQuery,
        fields: 'files(id, name, mimeType)',
        pageSize: 1000
      })
      
      if (!searchResponse.ok) {
        throw new Error(`Failed to search subfolders: ${searchResponse.status}`)
      }
      
      const result = await searchResponse.json()
      const folders = result.files || []
      
      console.log(`🔍 GoogleDriveCleanup: Found ${folders.length} subfolders in app directory`)
      
      // Group folders by name to find duplicates
      const folderGroups = {}
      for (const folder of folders) {
        const name = folder.name
        if (!folderGroups[name]) {
          folderGroups[name] = []
        }
        folderGroups[name].push(folder)
      }

      // Find duplicate subfolders
      const duplicateFolders = []
      for (const [name, folderList] of Object.entries(folderGroups)) {
        if (folderList.length > 1) {
          console.log(`🔍 GoogleDriveCleanup: Found ${folderList.length} subfolders with name "${name}"`)
          // Keep the first one, mark the rest as duplicates
          duplicateFolders.push(...folderList.slice(1))
        }
      }

      if (duplicateFolders.length === 0) {
        return {
          deletedCount: 0,
          message: 'No duplicate subfolders found'
        }
      }

      console.log(`🧹 GoogleDriveCleanup: Found ${duplicateFolders.length} duplicate subfolders to clean up`)
      
      // Delete duplicate subfolders
      let deletedCount = 0
      for (const folder of duplicateFolders) {
        try {
          // First, check if folder is empty
          const folderContentsQuery = `'${folder.id}' in parents and trashed=false`
          const folderContentsResponse = await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files`, {
            q: folderContentsQuery,
            fields: 'files(id, name)',
            pageSize: 1
          })
          
          if (!folderContentsResponse.ok) {
            console.warn(`⚠️ GoogleDriveCleanup: Failed to check subfolder contents for ${folder.name}`)
            continue
          }
          
          const folderContents = await folderContentsResponse.json()
          const folderContentsList = folderContents.files || []
          
          if (folderContentsList.length === 0) {
            // Empty folder, safe to delete
            await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files/${folder.id}`, null, { method: 'DELETE' })
            deletedCount++
            console.log(`🗑️ GoogleDriveCleanup: Deleted empty duplicate subfolder: ${folder.name}`)
          } else {
            console.log(`⚠️ GoogleDriveCleanup: Skipping non-empty duplicate subfolder: ${folder.name} (${folderContentsList.length} items)`)
          }
        } catch (error) {
          console.warn(`⚠️ GoogleDriveCleanup: Failed to delete subfolder ${folder.name}:`, error)
        }
      }

      return {
        deletedCount: deletedCount,
        message: `Cleaned up ${deletedCount} duplicate subfolders`
      }
    } catch (error) {
      console.error('❌ GoogleDriveCleanup: Failed to cleanup subfolders:', error)
      return {
        deletedCount: 0,
        message: `Subfolder cleanup failed: ${error.message}`
      }
    }
  }

  /**
   * Clean up all files from the soft-delete folder (permanently delete them)
   */
  async cleanupSoftDeletedNotes() {
    try {
      console.log('🗑️ GoogleDriveCleanup: Starting soft-delete folder cleanup...')
      
      // Ensure app folder structure is ready
      await this.driveAPI.ensureAppFolderStructure()
      
      // Get the soft-delete folder ID
      const deletedFolderId = this.driveAPI.folderCache.get('DELETED')
      if (!deletedFolderId) {
        throw new Error('Soft-delete folder not found')
      }
      
      console.log(`🔍 GoogleDriveCleanup: Found soft-delete folder: ${deletedFolderId}`)
      
      // Get all files in the soft-delete folder
      const query = `'${deletedFolderId}' in parents and trashed=false`
      const searchResponse = await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files`, {
        q: query,
        fields: 'files(id, name, size, mimeType)',
        pageSize: 1000
      })
      
      if (!searchResponse.ok) {
        throw new Error(`Failed to search soft-delete folder: ${searchResponse.status}`)
      }
      
      const result = await searchResponse.json()
      const files = result.files || []
      
      if (files.length === 0) {
        console.log('✅ GoogleDriveCleanup: Soft-delete folder is already empty')
        return {
          success: true,
          deletedCount: 0,
          message: 'Soft-delete folder is already empty'
        }
      }
      
      console.log(`🔍 GoogleDriveCleanup: Found ${files.length} files in soft-delete folder to permanently delete`)
      
      let deletedCount = 0
      let errorCount = 0
      
      // Permanently delete each file
      for (const file of files) {
        try {
          console.log(`🗑️ GoogleDriveCleanup: Permanently deleting file: ${file.name} (${file.id})`)
          
          // Permanently delete the file (not just move to trash)
          await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files/${file.id}`, null, { 
            method: 'DELETE' 
          })
          
          deletedCount++
          console.log(`✅ GoogleDriveCleanup: Successfully deleted file: ${file.name}`)
          
        } catch (error) {
          console.error(`❌ GoogleDriveCleanup: Failed to delete file ${file.name}:`, error)
          errorCount++
        }
      }
      
      console.log(`📊 GoogleDriveCleanup: Soft-delete cleanup complete! Deleted: ${deletedCount}, Errors: ${errorCount}`)
      
      return {
        success: true,
        deletedCount: deletedCount,
        errorCount: errorCount,
        message: `Successfully deleted ${deletedCount} files from soft-delete folder${errorCount > 0 ? ` (${errorCount} errors)` : ''}`
      }
      
    } catch (error) {
      console.error('❌ GoogleDriveCleanup: Failed to cleanup soft-delete folder:', error)
      return {
        success: false,
        deletedCount: 0,
        errorCount: 0,
        message: `Soft-delete cleanup failed: ${error.message}`
      }
    }
  }

  /**
   * Get statistics about orphaned images and attachments
   */
  async getOrphanedFilesStats() {
    if (!this.driveAPI.accessToken) {
      throw new Error('Google Drive API not initialized')
    }

    try {
      console.log('🔍 GoogleDriveCleanup: Scanning for orphaned images and attachments...')
      
      // Ensure app folder structure is ready
      await this.driveAPI.ensureAppFolderStructure()
      
      // Get all notes to extract referenced file IDs
      const notes = await this.getAllNotes()
      const referencedFileIds = new Set()
      const referencedAttachmentIds = new Set()
      
      // Extract file IDs from note content
      for (const note of notes) {
        if (note.content) {
          // Extract Google Drive image URLs
          const imageUrlRegex = /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9-_]+)\/view/g
          let match
          while ((match = imageUrlRegex.exec(note.content)) !== null) {
            referencedFileIds.add(match[1])
          }
          
          // Extract attachment file IDs from data attributes
          const attachmentRegex = /data-file-id="([^"]+)"/g
          while ((match = attachmentRegex.exec(note.content)) !== null) {
            referencedAttachmentIds.add(match[1])
          }
        }
      }
      
      console.log(`📊 GoogleDriveCleanup: Found ${referencedFileIds.size} referenced image file IDs and ${referencedAttachmentIds.size} referenced attachment IDs`)
      
      // Get all files in attachments folder
      const attachmentsFolderId = this.driveAPI.folderCache.get('ATTACHMENTS')
      let orphanedImages = 0
      let orphanedAttachments = 0
      let totalImages = 0
      let totalAttachments = 0
      
      if (attachmentsFolderId) {
        const attachmentsQuery = `'${attachmentsFolderId}' in parents and trashed=false`
        const attachmentsResponse = await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files`, {
          q: attachmentsQuery,
          fields: 'files(id, name, mimeType, size)',
          pageSize: 1000
        })
        
        if (attachmentsResponse.ok) {
          const attachmentsResult = await attachmentsResponse.json()
          const attachmentFiles = attachmentsResult.files || []
          
          for (const file of attachmentFiles) {
            if (file.mimeType && file.mimeType.startsWith('image/')) {
              totalImages++
              if (!referencedFileIds.has(file.id)) {
                orphanedImages++
              }
            } else {
              totalAttachments++
              if (!referencedAttachmentIds.has(file.id)) {
                orphanedAttachments++
              }
            }
          }
        }
      }
      
      // Check IndexedDB image cache for orphaned entries
      let orphanedCacheEntries = 0
      try {
        const { localStorageService } = await import('./localStorageService')
        const cacheEntries = await localStorageService.getAllImageCacheEntries()
        
        for (const entry of cacheEntries) {
          if (!referencedFileIds.has(entry.fileId)) {
            orphanedCacheEntries++
          }
        }
      } catch (error) {
        console.warn('⚠️ GoogleDriveCleanup: Failed to check image cache:', error)
      }
      
      const stats = {
        totalImages,
        totalAttachments,
        orphanedImages,
        orphanedAttachments,
        orphanedCacheEntries,
        referencedFileIds: referencedFileIds.size,
        referencedAttachmentIds: referencedAttachmentIds.size
      }
      
      console.log(`📊 GoogleDriveCleanup: Orphaned files stats:`, stats)
      return stats
      
    } catch (error) {
      console.error('❌ GoogleDriveCleanup: Failed to get orphaned files stats:', error)
      throw error
    }
  }

  /**
   * Clean up orphaned images and attachments
   */
  async cleanupOrphanedFiles() {
    if (!this.driveAPI.accessToken) {
      throw new Error('Google Drive API not initialized')
    }

    try {
      console.log('🧹 GoogleDriveCleanup: Starting cleanup of orphaned files...')
      
      // Get orphaned files stats
      const stats = await this.getOrphanedFilesStats()
      
      if (stats.orphanedImages === 0 && stats.orphanedAttachments === 0 && stats.orphanedCacheEntries === 0) {
        return {
          cancelled: false,
          cleanedImages: 0,
          cleanedAttachments: 0,
          cleanedCacheEntries: 0,
          message: 'No orphaned files found'
        }
      }
      
      console.log(`🧹 GoogleDriveCleanup: Found ${stats.orphanedImages} orphaned images, ${stats.orphanedAttachments} orphaned attachments, ${stats.orphanedCacheEntries} orphaned cache entries`)
      
      // Get all notes to extract referenced file IDs
      const notes = await this.getAllNotes()
      const referencedFileIds = new Set()
      const referencedAttachmentIds = new Set()
      
      // Extract file IDs from note content
      for (const note of notes) {
        if (note.content) {
          // Extract Google Drive image URLs
          const imageUrlRegex = /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9-_]+)\/view/g
          let match
          while ((match = imageUrlRegex.exec(note.content)) !== null) {
            referencedFileIds.add(match[1])
          }
          
          // Extract attachment file IDs from data attributes
          const attachmentRegex = /data-file-id="([^"]+)"/g
          while ((match = attachmentRegex.exec(note.content)) !== null) {
            referencedAttachmentIds.add(match[1])
          }
        }
      }
      
      // Clean up orphaned images and attachments from Google Drive
      const attachmentsFolderId = this.driveAPI.folderCache.get('ATTACHMENTS')
      let cleanedImages = 0
      let cleanedAttachments = 0
      
      if (attachmentsFolderId) {
        const attachmentsQuery = `'${attachmentsFolderId}' in parents and trashed=false`
        const attachmentsResponse = await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files`, {
          q: attachmentsQuery,
          fields: 'files(id, name, mimeType, size)',
          pageSize: 1000
        })
        
        if (attachmentsResponse.ok) {
          const attachmentsResult = await attachmentsResponse.json()
          const attachmentFiles = attachmentsResult.files || []
          
          for (const file of attachmentFiles) {
            let isOrphaned = false
            let fileType = ''
            
            if (file.mimeType && file.mimeType.startsWith('image/')) {
              fileType = 'image'
              isOrphaned = !referencedFileIds.has(file.id)
            } else {
              fileType = 'attachment'
              isOrphaned = !referencedAttachmentIds.has(file.id)
            }
            
            if (isOrphaned) {
              try {
                console.log(`🗑️ GoogleDriveCleanup: Deleting orphaned ${fileType}: ${file.name} (${file.id})`)
                
                // Move to trash instead of permanent delete
                await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files/${file.id}`, {}, {
                  method: 'PATCH',
                  body: JSON.stringify({ trashed: true }),
                  headers: { 'Content-Type': 'application/json' }
                })
                
                if (fileType === 'image') {
                  cleanedImages++
                } else {
                  cleanedAttachments++
                }
                
                console.log(`✅ GoogleDriveCleanup: Successfully deleted orphaned ${fileType}: ${file.name}`)
              } catch (error) {
                console.error(`❌ GoogleDriveCleanup: Failed to delete orphaned ${fileType} ${file.name}:`, error)
              }
            }
          }
        }
      }
      
      // Clean up orphaned cache entries from IndexedDB
      let cleanedCacheEntries = 0
      try {
        const { localStorageService } = await import('./localStorageService')
        const cacheEntries = await localStorageService.getAllImageCacheEntries()
        
        for (const entry of cacheEntries) {
          if (!referencedFileIds.has(entry.fileId)) {
            try {
              console.log(`🗑️ GoogleDriveCleanup: Deleting orphaned cache entry: ${entry.fileId}`)
              await localStorageService.deleteImageCache(entry.fileId)
              cleanedCacheEntries++
              console.log(`✅ GoogleDriveCleanup: Successfully deleted orphaned cache entry: ${entry.fileId}`)
            } catch (error) {
              console.error(`❌ GoogleDriveCleanup: Failed to delete orphaned cache entry ${entry.fileId}:`, error)
            }
          }
        }
      } catch (error) {
        console.warn('⚠️ GoogleDriveCleanup: Failed to clean up cache entries:', error)
      }
      
      console.log(`🧹 GoogleDriveCleanup: Orphaned files cleanup complete! Deleted ${cleanedImages} images, ${cleanedAttachments} attachments, ${cleanedCacheEntries} cache entries`)
      
      return {
        cancelled: false,
        cleanedImages,
        cleanedAttachments,
        cleanedCacheEntries,
        message: `Successfully cleaned up ${cleanedImages} orphaned images, ${cleanedAttachments} orphaned attachments, and ${cleanedCacheEntries} orphaned cache entries`
      }
      
    } catch (error) {
      console.error('❌ GoogleDriveCleanup: Failed to cleanup orphaned files:', error)
      throw error
    }
  }

  /**
   * Get all notes from the app folder
   */
  async getAllNotes() {
    try {
      // Ensure app folder structure is ready
      await this.driveAPI.ensureAppFolderStructure()
      
      // Get the notes folder ID
      const notesFolderId = this.driveAPI.folderCache.get('NOTES')
      if (!notesFolderId) {
        throw new Error('Notes folder not found')
      }
      
      // Get all note files
      const notesQuery = `'${notesFolderId}' in parents and trashed=false and name contains '.json'`
      const notesResponse = await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files`, {
        q: notesQuery,
        fields: 'files(id, name, mimeType)',
        pageSize: 1000
      })
      
      if (!notesResponse.ok) {
        throw new Error(`Failed to get notes: ${notesResponse.status}`)
      }
      
      const notesResult = await notesResponse.json()
      const noteFiles = notesResult.files || []
      
      // Download and parse each note
      const notes = []
      for (const noteFile of noteFiles) {
        try {
          const noteContentResponse = await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files/${noteFile.id}?alt=media`)
          if (noteContentResponse.ok) {
            const noteContent = await noteContentResponse.text()
            const note = JSON.parse(noteContent)
            notes.push(note)
          }
        } catch (error) {
          console.warn(`⚠️ GoogleDriveCleanup: Failed to parse note ${noteFile.name}:`, error)
        }
      }
      
      return notes
    } catch (error) {
      console.error('❌ GoogleDriveCleanup: Failed to get all notes:', error)
      throw error
    }
  }

  /**
   * Recursively delete a folder and all its contents
   */
  async deleteFolderRecursively(folderId) {
    try {
      console.log(`🗑️ GoogleDriveCleanup: Recursively deleting folder: ${folderId}`)
      
      // Get all contents of the folder
      const contentsQuery = `'${folderId}' in parents and trashed=false`
      const contentsResponse = await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files`, {
        q: contentsQuery,
        fields: 'files(id, name, mimeType)',
        pageSize: 1000
      })
      
      if (!contentsResponse.ok) {
        throw new Error(`Failed to get folder contents: ${contentsResponse.status}`)
      }
      
      const contents = await contentsResponse.json()
      const files = contents.files || []
      
      // Delete all contents first
      for (const file of files) {
        try {
          if (file.mimeType === 'application/vnd.google-apps.folder') {
            // Recursively delete subfolders
            await this.deleteFolderRecursively(file.id)
          } else {
            // Delete files
            await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files/${file.id}`, null, { method: 'DELETE' })
            console.log(`🗑️ GoogleDriveCleanup: Deleted file: ${file.name}`)
          }
        } catch (error) {
          console.warn(`⚠️ GoogleDriveCleanup: Failed to delete item ${file.name}:`, error)
        }
      }
      
      // Now delete the empty folder itself
      await this.driveAPI.apiRequest(`${this.driveAPI.API_BASE}/files/${folderId}`, null, { method: 'DELETE' })
      console.log(`✅ GoogleDriveCleanup: Successfully deleted folder: ${folderId}`)
      
    } catch (error) {
      console.error(`❌ GoogleDriveCleanup: Failed to recursively delete folder ${folderId}:`, error)
      throw error
    }
  }
}

// Export singleton instance
let cleanupInstance = null

export const getCleanupService = (googleDriveAPI) => {
  if (!cleanupInstance) {
    cleanupInstance = new GoogleDriveCleanup(googleDriveAPI)
  }
  return cleanupInstance
}

export default GoogleDriveCleanup 
